<template>
	<NuxtLayout
		name="hero-diagonal"
		:background-video="{
			mp4Src: cdnUrl('fit-hier-homepage-header_1080p_v1.mp4'),
			webmSrc: cdnUrl('fit-hier-homepage-header_1080p_v1.webm'),
			posterImg: {
				src: '/img/fitness/small-group-training_01.jpg',
				alt: 'Fit Hier Private Group Training',
				preload: true,
				width: 1920,
				height: 1490,
			},
		}"
		page-title-prefix="Private Group Training"
		page-title="Privé training met jouw groep"
		:breadcrumbs
	>
		<div class="flex flex-col gap-36 pb-36 md:pt-64">
			<div class="flex flex-col gap-36 sticky">
				<div class="lg:container flex flex-col md:grid md:grid-cols-3 gap-12">
					<div>
						<div class="sticky top-36 flex flex-col gap-8 px-12 md:px-0">
							<AtomCard class="p-12 flex flex-col gap-12 rounded-lg">
								<div class="flex flex-col gap-4">
									<AtomTitle
										tag="h2"
										size="xl"
									>
										Private Group Training
									</AtomTitle>
									<p class="text-functional-size-lg text-gray-600">
										Waarom Hier?
									</p>
								</div>
								<MoleculeIconList
									:items="[
										'Trainen met je eigen team of groep',
										'De gym exclusief voor jouw groep',
										'Volledig programma op maat',
										'Professionele coaching',
										'Teams/groepen vanaf 3 personen',
										'Focus op gezamenlijke doelen',
										'Maximale individuele progressie',
										'Flexibele planning in overleg',
										'Motiverende en veilige omgeving',
										'Elk kwartaal meten en wegen',
										'Gratis handdoeken & koffiebar inbegrepen',
									]"
								/>
							</AtomCard>
							<AtomButton
								size="lg"
								text="Informatie aanvragen"
								:variant="ButtonVariant.Green"
								icon-left="fa-envelope"
								:to="{ url: '#aanmelden', title: 'Private Group Training informatie aanvragen' }"
								@click="trackButtonClick('Page_PGT_CTA_Lead')"
							/>
						</div>
					</div>
					<div class="relative col-span-2 grid gap-12">
						<div class="col-span-2 flex flex-col gap-12 bg-gray-50 md:rounded-lg p-12">
							<div class="relative flex flex-col gap-8">
								<AtomTitle
									tag="h2"
									size="2xl"
								>
									Waarom trainen met Private Group Training?
								</AtomTitle>
								<p class="text-paragraph-size-lg">
									Private Group Training is exclusief trainen met je eigen groep. Jullie krijgen een volledig op maat gemaakt programma, afgestemd op gezamenlijke doelen en niveau. Met volledige aandacht van een trainer, werk je samen aan progressie, teamgevoel en resultaat.
								</p>
							</div>
							<div class="relative flex flex-col gap-8">
								<AtomTitle
									tag="h3"
									size="xl"
									class="mt-8"
								>
									Voor wie?
								</AtomTitle>
								<p>
									Private Group Training is er voor iedereen die samen wil sporten met een vaste groep. Of je nu traint met collega’s, vrienden, familie of een sportteam: jullie krijgen een programma dat past bij jullie doelen, niveau en voorkeuren. Persoonlijke begeleiding, structuur en plezier staan centraal, met zichtbare progressie als resultaat.
								</p>
							</div>
							<div class="relative flex flex-col gap-8">
								<AtomTitle
									tag="h4"
									size="lg"
								>
									Seizoensgebonden training voor sportteams en atleten
								</AtomTitle>
								<p>
									Met onze gerichte programma’s werken we aan kracht, mobiliteit, herstel en blessurepreventie. Van in-season onderhoud tot pre-season opbouw en post-season herstel: wij zorgen voor structuur, begeleiding en meetbare progressie. Altijd afgestemd op het team én de individuele sporter.
								</p>
								<AtomLink
									class="link flex items-center gap-2"
									url="#training-voor-sportteams"
									title="Meer over kracht- & conditietraining voor sportteams"
								>
									<span>Training specifiek voor atleten</span>
									<AtomFontAwesome
										icon="fa-arrow-right"
									/>
								</AtomLink>
							</div>
							<div class="relative flex flex-col gap-8">
								<AtomTitle
									tag="h4"
									size="lg"
								>
									Teams & bedrijven
								</AtomTitle>
								<p>
									Op zoek naar bedrijfsfitness? Versterk je team met structurele vitaliteitstrainingen. Met Private Group Training gaan jij en je collega’s aan de slag met persoonlijke en gezamenlijke doelen. Goed voor je gezondheid en de teamdynamiek!
								</p>
								<AtomLink
									class="link flex items-center gap-2"
									url="#bedrijfsfitness"
									title="Meer over sport & teambuilding voor jouw bedrijfsteam"
								>
									<span>Sport & teambuilding voor jouw bedrijfsteam</span>
									<AtomFontAwesome
										icon="fa-arrow-right"
									/>
								</AtomLink>
							</div>
							<div class="relative flex flex-col gap-8">
								<AtomTitle
									tag="h4"
									size="lg"
								>
									Vrienden, familie en gezinnen
								</AtomTitle>
								<p>
									Samen structureel werken aan een gezonde leefstijl. Onze trainingen helpen vriendengroepen, familie en gezinnen fitter te worden, met extra aandacht voor motivatie, binding en gezamenlijke groei.
								</p>
								<AtomLink
									class="link flex items-center gap-2"
									url="#sporten-met-vrienden-of-familie"
									title="Meer over sporten met familie of vrienden"
								>
									<span>Sporten met familie of vrienden</span>
									<AtomFontAwesome
										icon="fa-arrow-right"
									/>
								</AtomLink>
							</div>
						</div>
					</div>
				</div>

				<div class="container grid grid-cols-1 md:grid-cols-2 gap-8">
					<AtomImage
						src="/img/fitness/private-group-training_01.jpg"
						alt="Private Group Training"
						:width="640"
						:height="360"
						img-class="rounded aspect-video object-cover"
					/>
					<AtomImage
						src="/img/fitness/private-group-training_02.jpg"
						alt="Private Group Training"
						:width="640"
						:height="360"
						img-class="rounded aspect-video object-cover"
					/>
				</div>
			</div>

			<MoleculeBackgroundSection
				id="aanmelden"
				color="purple"
				class="lg:scroll-mt-72"
			>
				<div class="flex flex-col items-center gap-12">
					<AtomTitle
						tag="h2"
						size="3xl"
					>
						Private Group Training informatiepakket
					</AtomTitle>
					<div class="lg:w-3/4 flex flex-col gap-12">
						<p class="text-center">
							Wil je meer weten over Private Group Training bij Fit Hier? Vraag het informatiepakket aan en geef aan wat voor type groep jullie zijn. Je ontvangt binnen enkele minuten alle details per mail.
						</p>
						<AtomCard class="p-12 flex flex-col gap-12 rounded-lg text-black">
							<OrganismFormLeadPgt />
						</AtomCard>
					</div>
				</div>
			</MoleculeBackgroundSection>

			<div class="flex flex-col gap-64 py-32">
				<div
					id="training-voor-sportteams"
					class="flex flex-col gap-24 scroll-mt-36"
				>
					<div class="container flex flex-col gap-20">
						<div class="flex flex-col gap-6">
							<div class="flex flex-col gap-2">
								<AtomTitle
									tag="h2"
									size="2xl"
								>
									Training specifiek voor atleten & sportteams
								</AtomTitle>
								<p class=" text-gray-600 text-functional-size-lg">
									Maximale prestaties voor jouw team, het jaar rond.
								</p>
							</div>
							<p class="text-paragraph-size-lg">
								Voor sportteams die willen bouwen aan prestaties, continuïteit en blessurepreventie is één ding essentieel: gerichte training, afgestemd op het seizoen. Met onze programma’s voor in-season en off-season ondersteunen we atleten structureel in kracht, herstel en fysieke paraatheid. Elk traject is ontworpen om prestaties te verbeteren, blessures te voorkomen en het team klaar te stomen voor het volgende moment van piekbelasting.
							</p>
						</div>
					</div>
					<div class="container max-w-screen-2xl flex flex-col gap-12">
						<div class="grid grid-cols-1 md:grid-cols-3 gap-8">
							<AtomCard class="p-12 flex flex-col gap-8">
								<AtomTitle
									tag="h3"
									size="xl"
								>
									Pre-Season Training
								</AtomTitle>
								<p>
									Een goede voorbereiding is cruciaal voor een sterk seizoen. Met het Pre-Season Training programma bouwen we in 4 tot 6 weken gericht aan kracht, snelheid, mobiliteit en blessurepreventie. We leggen een stevige fysieke basis, zodat je team klaar is om vanaf de eerste wedstrijd te presteren. Alles wordt afgestemd op jullie sport, speelschema en individuele behoeften binnen het team.
								</p>
							</AtomCard>
							<AtomCard class="p-12 flex flex-col gap-8">
								<AtomTitle
									tag="h3"
									size="xl"
								>
									In-Season Training
								</AtomTitle>
								<p>
									Geen sportteam is hetzelfde, daarom stemmen we ons In-Season Training programma volledig af op de behoefte van jouw team. Samen brengen we de fysieke belasting in kaart, bespreken we specifieke doelen (zoals krachtbehoud, mobiliteit of blessurepreventie) en stellen we een gericht trainingsplan op dat perfect aansluit op het wedstrijdschema.
								</p>
							</AtomCard>
							<AtomCard class="p-12 flex flex-col gap-8">
								<AtomTitle
									tag="h3"
									size="xl"
								>
									Post-Season Training
								</AtomTitle>
								<p>
									Na een intensief seizoen is herstel essentieel. Het Post-Season Recovery programma helpt atleten in 2 tot 3 weken op een gecontroleerde manier te herstellen, zowel fysiek als mentaal. We combineren lichte actieve training met mobiliteit en herstelbevorderende sessies. Zo voorkom je stilstand en bereid je het lichaam voor op de volgende fase in de sportkalender.
								</p>
							</AtomCard>
						</div>
					</div>
				</div>

				<div
					id="bedrijfsfitness"
					class="flex flex-col gap-24 scroll-mt-36"
				>
					<div class="container flex flex-col gap-20">
						<div class="flex flex-col gap-6">
							<div class="flex flex-col gap-2">
								<AtomTitle
									tag="h2"
									size="2xl"
								>
									Sport & teambuilding voor jouw bedrijfsteam
								</AtomTitle>
								<p class=" text-gray-600 text-functional-size-lg">
									Voor een gezond, fit en sterk team.
								</p>
							</div>
							<p class="text-paragraph-size-lg">
								Een vitaal team is niet alleen gezonder en energieker, maar functioneert ook beter. Met Private Group Training werk je samen aan fysieke én mentale fitheid, wat bijdraagt aan betere prestaties en een sterkere teamdynamiek.
							</p>
						</div>
					</div>
					<div class="container max-w-screen-2xl flex flex-col gap-12">
						<div class="grid grid-cols-1 md:grid-cols-3 gap-8">
							<AtomCard class="p-12 flex flex-col gap-8">
								<AtomTitle
									tag="h3"
									size="xl"
								>
									Samen sterker door beweging
								</AtomTitle>
								<p>
									Samen met je collega’s trainen buiten de werkvloer zorgt voor informele momenten waarin jullie elkaar écht leren kennen. Die gedeelde ervaringen versterken de onderlinge band, maken samenwerken gemakkelijker en brengen plezier in het dagelijkse werk.
								</p>
							</AtomCard>
							<AtomCard class="p-12 flex flex-col gap-8">
								<AtomTitle
									tag="h3"
									size="xl"
								>
									Meer energie én gezonder
								</AtomTitle>
								<p>
									Regelmatig sporten met collega’s geeft iedereen een energieboost: je voelt je scherper, productiever en gemotiveerder. En door die investering in gezondheid is er minder ziekteverzuim, doordat rugklachten, burn-outklachten en andere werkgerelateerde klachten afnemen.
								</p>
							</AtomCard>
							<AtomCard class="p-12 flex flex-col gap-8">
								<AtomTitle
									tag="h3"
									size="xl"
								>
									Een gezonde bedrijfscultuur
								</AtomTitle>
								<p>
									Bedrijfssport laat zien dat welzijn centraal staat in de organisatie. Zo’n gezamenlijk initiatief versterkt de betrokkenheid, verhoogt de tevredenheid en maakt de organisatie aantrekkelijker. Voor huidige collega’s én toekomstige talenten!
								</p>
							</AtomCard>
						</div>
					</div>
				</div>

				<div
					id="sporten-met-vrienden-of-familie"
					class="flex flex-col gap-24 scroll-mt-36"
				>
					<div class="container flex flex-col gap-20">
						<div class="flex flex-col gap-6">
							<div class="flex flex-col gap-2">
								<AtomTitle
									tag="h2"
									size="2xl"
								>
									Sporten met familie of vrienden
								</AtomTitle>
								<p class=" text-gray-600 text-functional-size-lg">
									Gezamenlijke doelen bereik je Hier.
								</p>
							</div>
							<p class="text-paragraph-size-lg">
								Sporten met familie of vrienden is de ideale manier om samen te werken aan een gezonde leefstijl. Of je met je beste vrienden je conditie wil verbeteren, met je partner sterker wil worden of met het hele gezin plezier wil beleven, onze Private Group Training biedt maatwerk op jullie persoonlijke doelen. Samen trainen zorgt voor extra motivatie, versterkt de onderlinge band en houdt jullie scherp dankzij regelmatige voortgangsmetingen.
							</p>
						</div>
						<div class="grid grid-cols-1 md:grid-cols-2 gap-8">
							<AtomCard class="p-12 flex flex-col gap-8">
								<AtomTitle
									tag="h3"
									size="xl"
								>
									Breng je gezin in beweging!
								</AtomTitle>
								<p>
									Ga samen met je gezin voor je gezondste zelf én versterk jullie onderlinge band! Met onze Private Group Training krijgt ieder gezinslid een uitdagend, persoonlijk programma en houden regelmatige voortgangsmetingen jullie scherp. Zo vieren jullie elke mijlpaal samen en blijft bewegen een feest.
								</p>
							</AtomCard>
							<AtomCard class="p-12 flex flex-col gap-8">
								<AtomTitle
									tag="h3"
									size="xl"
								>
									Daag je vrienden uit!
								</AtomTitle>
								<p>
									Til je vriendschap naar een hoger niveau door samen te trainen! Voor vriendengroepen van vier of meer stemmen we elke sessie af op jullie ambities en motiveren kwartaalrapportages jullie om door te zetten. Zo beleef je niet alleen plezier, maar boek je samen indrukwekkende resultaten.
								</p>
							</AtomCard>
						</div>
					</div>
				</div>
			</div>
		</div>

		<div class="container flex flex-col gap-32 md:gap-64 md:py-32">
			<CompositionActionButtonsPgt1 />
			<div class="flex flex-col gap-12 items-center">
				<AtomTitle
					tag="h2"
					size="3xl"
					class="text-center"
				>
					Onze leden aan het woord
				</AtomTitle>

				<OrganismReviewSlideshow :reviews="reviews" />
			</div>
		</div>

		<div class="container max-w-screen-md flex flex-col gap-12 pb-36">
			<AtomTitle
				tag="h2"
				size="3xl"
				class="text-center"
			>
				Veelgestelde vragen
			</AtomTitle>
			<OrganismFaq :items="pgtFaqItems" />
		</div>
	</NuxtLayout>
</template>

<script lang="ts" setup>
import { cdnUrl } from '#base/helpers/contentHelpers';
import reviews from '#fit/core/consts/reviews';
import { pgtFaqItems } from '#fit/core/consts/faqItems';
import type { BreadcrumbsType } from '#base/core/types/BreadcrumbsType';
import { ButtonVariant } from '#base/core/enums/ButtonVariantEnum';

useSeoMeta({
	title: 'Private Group Training – Train met jouw Team | Fit Hier Uden',
	ogTitle: 'Private Group Training – Train met jouw team | Fit Hier Uden',
	description: 'Privé groepstraining in Uden met je eigen team, vrienden of collega\'s. Maatwerkprogramma, coaching en flexibele planning. Plan een kennismaking.',
});

const breadcrumbs: BreadcrumbsType = [
	{
		text: 'Home',
		to: {
			title: 'Home',
			url: '/',
		},
	},
	{
		text: 'Private Group Training',
		to: {
			url: '/private-group-training',
			title: 'Private Group Training',
		},
	},
];

const { trackButtonClick } = useEventTracking();
</script>
